import {
    deleteUser,
    getUserList,
    registerUserInformation,
    updateUserInformation,
    getUserInformation
} from "./api";
import { UserId } from "../../../../domain/user/UserId";
import { UserName } from "../../../../domain/user/UserName";
import { UserAccountRole } from "../../../../domain/user/account/UserAccountRole";
import { UserEmail } from "../../../../domain/user/UserEmail";
import { CareRecipientId } from "../../../../domain/care_recipient/CareRecipientId";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { orderErrorToast, orderSuccessToast } from "../../../../app/funcs/orderResultToast";
import { UserPassword } from "../../../../domain/user/UserPassword";

export const ActionType = Object.freeze({
    base: "@@Monitoring/Action/Admin/User",
    getList: "getList",
    getInfo: "getInfo",
    init: "init",
    register: "register",
    update: "update",
    delete: "delete"
});

export const getUserListAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getList}`,
    async (_, { rejectWithValue }) => {
        const response = await getUserList();

        if (response.error === undefined) {
            // API処理結果正常の場合
            return { ...response };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }
    }
);

export const getUserInformationAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getInfo}`,
    async (userId: UserId, { rejectWithValue }) => {
        const response = await getUserInformation(userId);

        if (response.error === undefined) {
            // API処理結果正常の場合
            return {
                userInformation: response.user,
                viewableCareRecipients: response.viewableCareRecipients
            };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }
    }
);

export const initUserInformationAction = createAction(`${ActionType.base}/${ActionType.init}`);

export const registerUserInformationAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.register}`,
    async (
        data: {
            userName: UserName,
            userEmail: UserEmail,
            userPassword: UserPassword,
            role: UserAccountRole,
            viewableCareRecipients: CareRecipientId[],
            sendEmail: boolean,
            onSuccess: () => void,
            onFailure: (errorCode: string) => void
        },
        { rejectWithValue }
    ) => {
        const response = await registerUserInformation(
            data.userName,
            data.userEmail,
            data.userPassword,
            data.role,
            data.viewableCareRecipients,
            data.sendEmail
        );

        if (response.error === undefined) {
            // API処理結果正常の場合
            orderSuccessToast("ユーザーを登録しました。");
            data.onSuccess();
            return {};
        } else {
            // API処理結果異常の場合
            orderErrorToast("ユーザーの登録に失敗しました。");
            data.onFailure(response.error.code);
            return rejectWithValue({});
        }
    }
);

export const updateUserInformationAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.update}`,
    async (
        data: {
            userId: UserId,
            userName: UserName,
            viewableCareRecipients: CareRecipientId[]
        },
        { rejectWithValue }
    ) => {
        const response = await updateUserInformation(
            data.userId,
            data.userName,
            data.viewableCareRecipients,
        );

        if (response.error === undefined) {
            // API処理結果正常の場合
            orderSuccessToast("ユーザーを更新しました。");
            return {};
        } else {
            // API処理結果異常の場合
            orderErrorToast("ユーザーの更新に失敗しました。");
            return rejectWithValue({});
        }
    }
);

export const deleteUserAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.delete}`,
    async (
        data: {
            userId: UserId
        },
        { rejectWithValue }
    ) => {
        const response = await deleteUser(data.userId);

        if (response.error === undefined) {
            // API処理結果正常の場合
            orderSuccessToast("ユーザーを削除しました。");
            return { userId: data.userId.value };
        } else {
            // API処理結果異常の場合
            orderErrorToast("ユーザーの削除に失敗しました。");
            return rejectWithValue({});
        }
    }
);
